import {
  Application,
  Container,
  Graphics,
  Sprite,
  Texture,
  TilingSprite,
} from "pixi.js";
import { Player } from "./entities/player";
import grass from "../assets/img/tile/grass.png";
import { Ratfink } from "./entities/ratfink";
import { Inventory } from "./inventory";
import { Scanner } from "./entities/scanner";
import { water } from "./spritesheets/water";
import { loadSound, playSound } from "./audio";

export type Coordinate = { x: number; y: number };

export interface GameObj {
  position: Coordinate;
}

const shouldDrawGrid = false;

export interface Character extends GameObj {
  id: string;
  tick: () => void;
  initSprites: () => Promise<void>;
  catch: () => void;
  release: (position: Coordinate) => void;
  sprite: Sprite;
}

const GRID_WIDTH = 20;
const GRID_HEIGHT = 15;

//https://www.beepbox.co/#9n31sbk7l00e0ft2ma7g0fj07r1i0o542T0v3u10f0qg01d04w2h0E0T3v0u03f21j00x7q6x11m0ci1d00SU0M51pp9aiirijrE20686T3v3u03f12rcq2x10x5f0d00SU0M51pr2qiqqrrrE2b686T4v1uf0f0q011z6666ji8k8k3jSBKSJJAArriiiiii07JCABrzrrrrrrr00YrkqHrsrrrrjr005zrAqzrjzrrqr1jRjrqGGrrzsrsA099ijrABJJJIAzrrtirqrqjqixzsrAjrqjiqaqqysttAJqjikikrizrHtBJJAzArzrIsRCITKSS099ijrAJS____Qg99habbCAYrDzh00E0b8i4xci4xhj4h4h4h4h4h4g004h4h4h4h4h400000000p23VIQuSO8TJ7-65Gqf9qidPhZnx6Ph_shYlurNR97NCoh-5c4v51wAf1bihYlu4v7shYlurNR97NCoh-eUhYnuXNRkBNuWhYcxohYuM8Fs8SkYpC4vxCaN7Mh10An19jjNlUhYtN7NlVT7kQYpC4vxEb17MhxYgs4tdf5nx7NT4v5nDstjjNCohyZBUg005dtToJlToJlTpjntSbntSbntSkRTtyRTtyRTtBdtToJlToE2VE-1a7dRlhPo00
const theme = loadSound("theme");

export const pixiApplication = async (parent: string) => {
  const app = new Application();

  await app.init({
    resizeTo: document.getElementById(parent),
    backgroundColor: 0x000000,
  });

  const stage: Container = new Container();

  const blockWidth = app.canvas.width / GRID_WIDTH;
  const blockHeight = app.canvas.height / GRID_HEIGHT;

  const tilingSprite = new TilingSprite({
    texture: Texture.from(grass),
    width: app.canvas.width,
    height: app.canvas.height,
    scale: blockWidth / 16,
  });

  theme.loop = true;
  playSound(theme, "MUSIC");

  stage.addChild(tilingSprite);

  const inventory = new Inventory(
    { width: app.canvas.width, height: app.canvas.height },
    { blockWidth, blockHeight },
  );

  stage.addChild(inventory.container);

  let characters: Array<{ character: Character; position: Coordinate }> = [];

  const swipe = (position: Coordinate) => {
    if (inventory.caughtLad === undefined) {
      for (const { character } of characters) {
        if (
          Math.abs(character.sprite.x - blockWidth * position.x) <
            blockWidth / 2 &&
          Math.abs(character.sprite.y - blockHeight * position.y) <
            blockHeight / 2
        ) {
          inventory.catch(character);
        }
      }
    } else {
      inventory.caughtLad.release(position);
      inventory.caughtLad.sprite.position.set(
        position.x * blockWidth,
        position.y * blockWidth,
      );
      if (position.x === 5 && (position.y === 5 || position.y === 6)) {
        scanner.mode = "grind";
        characters = characters.filter(
          ({ character }) => character.id !== inventory.caughtLad.id,
        );
        inventory.caughtLad.catch();
      }
      inventory.release();
    }
  };

  characters.push(
    { character: new Player(app, swipe), position: { x: 10, y: 7 } },
    { character: new Ratfink(), position: { x: 10, y: 10 } },
    { character: new Ratfink(), position: { x: 12, y: 2 } },
    { character: new Ratfink(), position: { x: 13, y: 14 } },
  );

  const noGoZones = [
    // scanner
    { x: 5, y: 5 },
    { x: 5, y: 6 },
    { x: 6, y: 5 },
    { x: 6, y: 6 },
    // river
    { x: 15, y: 0 },
    { x: 15, y: 1 },
    { x: 15, y: 2 },
    { x: 15, y: 3 },
    { x: 15, y: 4 },
    { x: 15, y: 5 },
    { x: 15, y: 6 },
    { x: 15, y: 7 },
    { x: 15, y: 8 },
    { x: 15, y: 9 },
    { x: 15, y: 10 },
    { x: 15, y: 11 },
    { x: 15, y: 12 },
    { x: 15, y: 13 },
    { x: 15, y: 14 },
    { x: 15, y: 15 },
    { x: 16, y: 0 },
    { x: 16, y: 1 },
    { x: 16, y: 2 },
    { x: 16, y: 3 },
    { x: 16, y: 4 },
    { x: 16, y: 5 },
    { x: 16, y: 6 },
    { x: 16, y: 7 },
    { x: 16, y: 8 },
    { x: 16, y: 9 },
    { x: 16, y: 10 },
    { x: 16, y: 11 },
    { x: 16, y: 12 },
    { x: 16, y: 13 },
    { x: 16, y: 14 },
    { x: 16, y: 15 },
  ];

  for (let r = 0; r < 16; r++) {
    const riverSprite1 = await water();
    riverSprite1.scale = blockWidth / 16;
    riverSprite1.onFrameChange = () => {
      riverSprite1.scale = blockWidth / 16;
    };
    riverSprite1.animationSpeed = 0.01;
    riverSprite1.play();
    riverSprite1.x = 15 * blockWidth;
    riverSprite1.y = r * blockHeight;

    const riverSprite2 = await water();
    riverSprite2.scale = blockWidth / 16;
    riverSprite2.onFrameChange = () => {
      riverSprite2.scale = blockWidth / 16;
    };
    riverSprite2.animationSpeed = 0.01;
    riverSprite2.play();
    riverSprite2.x = 16 * blockWidth;
    riverSprite2.y = r * blockHeight;

    stage.addChild(riverSprite1);
    stage.addChild(riverSprite2);
  }

  initCharacters(characters, { blockWidth, blockHeight }, stage);

  const scanner = new Scanner({ x: 5, y: 5 });
  await scanner.initSprites();

  if (shouldDrawGrid) {
    stage.addChild(drawGrid(blockWidth, blockHeight));
  }

  app.renderer.render(stage);

  function gameLoop(): void {
    requestAnimationFrame(gameLoop);
    app.renderer.render(stage);
  }

  app.ticker.add(({ deltaTime }) => {
    const width = app.canvas.width;
    const height = app.canvas.height;

    const blockWidth = width / GRID_WIDTH;
    const blockHeight = height / GRID_HEIGHT;

    characters.forEach(({ character }) => {
      tickCharacter(
        character,
        { blockWidth, blockHeight },
        deltaTime,
        stage,
        noGoZones,
      );
    });

    scanner.sprite.x = scanner.position.x * blockWidth;
    scanner.sprite.y = scanner.position.y * blockHeight;
    stage.removeChild(scanner.sprite);
    scanner.tick();
    stage.addChild(scanner.sprite);
  });

  gameLoop();

  return app.canvas;
};

const initCharacters = async (
  characters: { character: Character; position: Coordinate }[],
  { blockWidth }: { blockWidth: number; blockHeight: number },
  stage: Container,
) => {
  characters.forEach(async ({ character: c, position: p }) => {
    await c.initSprites();
    c.position = p;
    c.sprite.position.set(p.x * blockWidth, p.y * blockWidth);
    stage.addChild(c.sprite);
  });
};

const tickCharacter = (
  character: Character,
  { blockWidth, blockHeight }: { blockWidth: number; blockHeight: number },
  deltaTime: number,
  stage: Container,
  noGoZones: Coordinate[],
) => {
  // tick character, shuffle sprites
  if (
    character.sprite.x === character.position.x * blockWidth &&
    character.sprite.y === character.position.y * blockHeight
  ) {
    const oldSprite = character.sprite;
    stage.removeChild(character.sprite);
    character.tick();
    stage.addChild(character.sprite);
    character.sprite.position = oldSprite.position;
  }

  if (
    noGoZones.find(
      (z) => z.x === character.position.x && z.y === character.position.y,
    )
  ) {
    character.position = {
      x: Math.round(character.sprite.x / blockWidth),
      y: Math.round(character.sprite.y / blockHeight),
    };
  }
  // keep inside the grid
  character.position.x = Math.max(0, character.position.x);
  character.position.x = Math.min(GRID_WIDTH, character.position.x);
  character.position.y = Math.max(0, character.position.y);
  character.position.y = Math.min(GRID_HEIGHT, character.position.y);

  // move character from actual position to intended position
  const targetX = character.position.x * blockWidth;
  const targetY = character.position.y * blockHeight;

  const speedX = (deltaTime * blockWidth) / 20;
  const speedY = (deltaTime * blockWidth) / 20;

  if (character.sprite.x < targetX) {
    character.sprite.x = Math.min(character.sprite.x + speedX, targetX);
  } else if (character.sprite.x > targetX) {
    character.sprite.x = Math.max(character.sprite.x - speedX, targetX);
  }
  if (character.sprite.y < targetY) {
    character.sprite.y = Math.min(character.sprite.y + speedY, targetY);
  } else if (character.sprite.y > targetY) {
    character.sprite.y = Math.max(character.sprite.y - speedY, targetY);
  }
};

const drawGrid = (blockWidth: number, blockHeight: number) => {
  const g = new Graphics();
  g.zIndex = 10000;

  for (let x = 1; x < GRID_WIDTH; x++) {
    g.moveTo(x * blockWidth, 0);
    g.lineTo(x * blockWidth, GRID_HEIGHT * blockHeight);
  }
  for (let y = 1; y < GRID_HEIGHT; y++) {
    g.moveTo(0, y * blockHeight);
    g.lineTo(GRID_WIDTH * blockWidth, y * blockHeight);
  }

  g.stroke({ width: 1, color: "#000033" });

  return g;
};
